// src/features/records/services/signedUrlService.js

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

/**
 * Service for managing signed URLs for attachment access
 * 
 * Features:
 * - In-memory caching with expiration tracking
 * - Batch requests for efficiency
 * - Automatic retry on 403 errors
 * - 5-minute safety buffer for expiration
 */
class SignedUrlService {
  constructor() {
    // Initialize the Cloud Function
    this.signUrlsFn = httpsCallable(functions, 'signUrls');
    
    // In-memory cache: Map<storagePath, {url: string, expiresAt: number}>
    this.cache = new Map();
    
    // Safety buffer: 5 minutes before actual expiration
    this.EXPIRY_BUFFER_MS = 5 * 60 * 1000;
    
    // Batch configuration
    this.MAX_BATCH_SIZE = 30;
    
    // Maximum cache entries to prevent unbounded growth
    this.MAX_CACHE_ENTRIES = 2000;
    
    // Pending requests to prevent duplicate calls
    this.pendingRequests = new Map();
  }

  /**
   * Move entry to end of Map for LRU and evict oldest if over capacity
   * @param {string} key - Cache key
   * @param {Object} val - Cache value
   */
  touchLRU(key, val) {
    // Move to end by deleting and re-adding
    this.cache.delete(key);
    this.cache.set(key, val);
    
    // Evict oldest if over capacity
    if (this.cache.size > this.MAX_CACHE_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Check if a cached URL is still valid
   * @param {Object} cached - Cached URL object
   * @returns {boolean} True if still valid
   */
  isValidCached(cached) {
    if (!cached || !cached.url || !cached.expiresAt) {
      return false;
    }
    
    // Check if URL expires in more than 5 minutes
    const now = Date.now();
    return cached.expiresAt - now > this.EXPIRY_BUFFER_MS;
  }

  /**
   * Get entry from cache with LRU refresh and expired entry cleanup
   * @param {string} path - Storage path
   * @returns {Object|null} Cached entry or null
   */
  getFromCache(path) {
    const entry = this.cache.get(path);
    if (this.isValidCached(entry)) {
      // LRU refresh - move to end
      this.touchLRU(path, entry);
      return entry;
    }
    // Drop expired entries to avoid unbounded growth of dead entries
    if (entry) {
      this.cache.delete(path);
    }
    return null;
  }

  /**
   * Get a signed URL for a single storage path
   * @param {string} storagePath - Path to the file in storage
   * @returns {Promise<string>} Signed URL
   */
  async getSignedUrl(storagePath) {
    if (!storagePath) {
      throw new Error('Storage path is required');
    }

    // Check cache first with LRU refresh
    const cached = this.getFromCache(storagePath);
    if (cached) {
      return cached.url;
    }

    // Check if there's already a pending request for this path
    if (this.pendingRequests.has(storagePath)) {
      return this.pendingRequests.get(storagePath);
    }

    // Create request promise
    const requestPromise = this.fetchSignedUrls([storagePath])
      .then(results => {
        const result = results[0];
        if (result && result.url) {
          // Cache with LRU on successful fetch
          if (result.expiresAt) {
            this.touchLRU(storagePath, {
              url: result.url,
              expiresAt: result.expiresAt
            });
          }
          return result.url;
        }
        throw new Error(result?.error || 'Failed to get signed URL');
      })
      .finally(() => {
        this.pendingRequests.delete(storagePath);
      });

    // Store pending request
    this.pendingRequests.set(storagePath, requestPromise);

    return requestPromise;
  }

  /**
   * Get signed URLs for multiple storage paths
   * @param {string[]} storagePaths - Array of storage paths
   * @returns {Promise<Object[]>} Array of {path, url, expiresAt} or {path, error}
   */
  async getSignedUrls(storagePaths) {
    if (!Array.isArray(storagePaths) || storagePaths.length === 0) {
      return [];
    }

    // Remove duplicates
    const uniquePaths = [...new Set(storagePaths)];
    
    // Separate cached and uncached paths
    const results = [];
    const uncachedPaths = [];

    for (const path of uniquePaths) {
      const cached = this.getFromCache(path);
      if (cached) {
        results.push({
          path,
          url: cached.url,
          expiresAt: cached.expiresAt
        });
      } else {
        uncachedPaths.push(path);
      }
    }

    // If all are cached, return immediately
    if (uncachedPaths.length === 0) {
      return results;
    }

    // Fetch uncached URLs in batches
    const batches = [];
    for (let i = 0; i < uncachedPaths.length; i += this.MAX_BATCH_SIZE) {
      batches.push(uncachedPaths.slice(i, i + this.MAX_BATCH_SIZE));
    }

    const batchResults = await Promise.all(
      batches.map(batch => this.fetchSignedUrls(batch))
    );

    // Flatten batch results and combine with cached results
    const fetchedResults = batchResults.flat();
    return [...results, ...fetchedResults];
  }

  /**
   * Fetch signed URLs from the server
   * @param {string[]} paths - Storage paths to sign
   * @returns {Promise<Object[]>} Array of results
   */
  async fetchSignedUrls(paths) {
    try {
      const response = await this.signUrlsFn({ paths });
      const { results } = response.data;

      // Cache successful results with LRU
      for (const result of results) {
        if (result.url && result.expiresAt) {
          this.touchLRU(result.path, {
            url: result.url,
            expiresAt: result.expiresAt
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error fetching signed URLs:', error);
      
      // Return error results for all paths
      return paths.map(path => ({
        path,
        url: null,
        error: error.message || 'Failed to fetch signed URL'
      }));
    }
  }

  /**
   * Clear the cache (useful for testing or memory management)
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache size (for monitoring)
   */
  getCacheSize() {
    return this.cache.size;
  }

  /**
   * Refresh a URL that returned 403
   * @param {string} storagePath - Storage path to refresh
   * @returns {Promise<string>} New signed URL
   */
  async refreshUrl(storagePath) {
    // Remove from cache to force refresh
    this.cache.delete(storagePath);
    
    // Get new URL
    return this.getSignedUrl(storagePath);
  }
}

// Create singleton instance
const signedUrlService = new SignedUrlService();

// Export service instance and convenience methods
export default signedUrlService;

export const getSignedUrl = (storagePath) => signedUrlService.getSignedUrl(storagePath);
export const getSignedUrls = (storagePaths) => signedUrlService.getSignedUrls(storagePaths);
export const refreshUrl = (storagePath) => signedUrlService.refreshUrl(storagePath);